import { useCallback, useEffect, useState } from 'react'

import { useServerTimeOffsetMs } from '~/hooks/useServerTimeOffsetMs'
import type { CivilDate } from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'

/**
 * JST は固定 +9h (DST なし)。実行環境のタイムゾーンに依存させない
 * (`src/models/weather/time.ts` の JST_OFFSET_MS 踏襲)。
 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000

function todayJstCivilDate(): CivilDate {
  const jst = new Date(serverNow().getTime() + JST_OFFSET_MS)
  return {
    year: jst.getUTCFullYear(),
    month: jst.getUTCMonth() + 1,
    day: jst.getUTCDate(),
  }
}

/**
 * JST 基準の「今日」を返す hook (occto-master-valid-period-6tabs 設計書 §5.4)。
 * 1 分間隔で再評価するため、画面を開いたまま日付を跨いでも失効・適用前判定が
 * stale にならない。
 *
 * `enabled = false` を渡すと 1 分 interval を張らずマウント時点の値を保持する
 * (= 呼び出し側で日付監視が不要な合成 hook から呼ばれるケース。例: 失効フィルタ
 * を持たないタブが `useExpiryFilter` を rules-of-hooks 回避のため常に呼ぶが
 * タイマーは不要な経路)。
 *
 * server time offset は session RPC 完了時 (= 初回描画より後) に確定しうるため、
 * `useServerTimeOffsetMs` を購読して確定時に取り直す (`~/models/clock` の規律:
 * 現在時刻を state に保持する consumer は offset 変化を依存に入れる)。 interval
 * 頼りだと確定まで最大 60 秒ズレ、 `enabled = false` の経路では interval を張らない
 * ため永久に再評価されない。
 */
export function useTodayJst(enabled = true): CivilDate {
  const [today, setToday] = useState(todayJstCivilDate)
  const offsetMs = useServerTimeOffsetMs()

  // 日付が変わらない再評価で毎回 再描画しないよう、値が同じなら前の参照を保つ。
  const refreshToday = useCallback(() => {
    setToday((prev) => {
      const next = todayJstCivilDate()
      return prev.year === next.year &&
        prev.month === next.month &&
        prev.day === next.day
        ? prev
        : next
    })
  }, [])

  useEffect(() => {
    refreshToday()
  }, [offsetMs, refreshToday])

  useEffect(() => {
    if (!enabled) {
      return
    }
    const interval = setInterval(refreshToday, 60_000)
    return (): void => clearInterval(interval)
  }, [enabled, refreshToday])

  return today
}
